import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Radar as RadarIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Assessment as AssessmentIcon,
  Public as PublicIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ATSSystemDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/pi-planning');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to PI Planning Board"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Automated Targeting System (ATS)
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            CBP Border Security and Risk Assessment Platform
          </Typography>
        </Box>
      </Box>

      {/* System Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            System Overview
          </Typography>
          <Typography variant="body1" paragraph>
            The Automated Targeting System (ATS) is a large-scale, multi-modal risk assessment platform operated by 
            U.S. Customs and Border Protection (CBP) under the Targeting and Analysis Systems Program Directorate (TASPD). 
            It consolidates traveler, cargo, and conveyance data to enable real-time risk scoring and screening at ports 
            of entry and international transit hubs.
          </Typography>
        </CardContent>
      </Card>

      {/* 5Ws Summary - Compact Table Format */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Overview: The 5Ws
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="ATS System 5Ws Overview">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '15%', bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="What is ATS"
                >
                  What
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    A decision-support and risk-assessment system that standardizes and analyzes data from various sources 
                    (TECS, ACS, AES, ADIS, PNR, etc.) to determine potential threats associated with travelers, conveyances, 
                    or shipments.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Who operates ATS"
                >
                  Who
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Operated by <strong>CBP's Office of Information and Technology (OIT)</strong> under the Targeting and Analysis Systems 
                    Program Directorate (TASPD). Supports analysts, targeting officers, DHS components (TSA, ICE, USCG), 
                    and external partners.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="When was ATS developed"
                >
                  When
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Developed <strong>post-9/11</strong> to enhance U.S. border and cargo security; continuously updated to integrate new 
                    data sources and algorithms for modern threat detection.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Where is ATS deployed"
                >
                  Where
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Deployed across CBP's <strong>national border operations</strong>, ports of entry, and targeting centers, including the 
                    <strong> National Targeting Center (NTC)</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Why ATS exists"
                >
                  Why
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    To <strong>prevent entry of terrorists, criminals, and contraband</strong>; and to improve 
                    intelligence integration for trade and travel facilitation.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Major Subsystems */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        ATS Major Subsystems
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%',
              border: '2px solid #003366',
              cursor: 'pointer',
              '&:hover': { 
                boxShadow: 6,
                transform: 'translateY(-4px)',
                transition: 'all 0.3s ease'
              }
            }}
            onClick={() => navigate('/admin/ats-cargo-system')}
            role="button"
            tabIndex={0}
            aria-label="View ATS-Cargo details"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/admin/ats-cargo-system');
              }
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                ATS-Cargo (ATS-C)
              </Typography>
              <Typography variant="body2" paragraph>
                Inbound/outbound cargo targeting; uses electronic bills of lading, manifest, and export data.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Scores shipments via analytics and rules</Typography></li>
                <li><Typography variant="body2">Identifies contraband and terrorism threats</Typography></li>
                <li><Typography variant="body2">Detects trade violations</Typography></li>
              </Box>
              <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold', mt: 2, display: 'block' }}>
                Click to view detailed subsystem information →
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%',
              border: '2px solid #1976D2',
              cursor: 'pointer',
              '&:hover': { 
                boxShadow: 6,
                transform: 'translateY(-4px)',
                transition: 'all 0.3s ease'
              }
            }}
            onClick={() => navigate('/admin/ats-passenger-system')}
            role="button"
            tabIndex={0}
            aria-label="View ATS-Passenger details"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/admin/ats-passenger-system');
              }
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
                ATS-Passenger (ATS-P)
              </Typography>
              <Typography variant="body2" paragraph>
                Assesses air, land, and sea travelers and crew.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Interfaces with PNR, SEVIS, ADIS data</Typography></li>
                <li><Typography variant="body2">Watchlist data integration</Typography></li>
                <li><Typography variant="body2">Produces real-time traveler risk scores</Typography></li>
              </Box>
              <Typography variant="caption" sx={{ color: '#1976D2', fontWeight: 'bold', mt: 2, display: 'block' }}>
                Click to view detailed subsystem information →
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card 
            sx={{ 
              height: '100%',
              border: '2px solid #2E7D32',
              cursor: 'pointer',
              '&:hover': { 
                boxShadow: 6,
                transform: 'translateY(-4px)',
                transition: 'all 0.3s ease'
              }
            }}
            onClick={() => navigate('/admin/ats-land-system')}
            role="button"
            tabIndex={0}
            aria-label="View ATS-Land details"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/admin/ats-land-system');
              }
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
                ATS-Land (ATS-L)
              </Typography>
              <Typography variant="body2">
                Focuses on private vehicle crossings at land borders; integrated with CBP land POEs.
              </Typography>
              <Typography variant="caption" sx={{ color: '#2E7D32', fontWeight: 'bold', mt: 2, display: 'block' }}>
                Click for details →
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card 
            sx={{ 
              height: '100%',
              border: '2px solid #F57C00',
              cursor: 'pointer',
              '&:hover': { 
                boxShadow: 6,
                transform: 'translateY(-4px)',
                transition: 'all 0.3s ease'
              }
            }}
            onClick={() => navigate('/admin/ats-international-system')}
            role="button"
            tabIndex={0}
            aria-label="View ATS-International details"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/admin/ats-international-system');
              }
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#F57C00' }}>
                ATS-International (ATS-I)
              </Typography>
              <Typography variant="body2">
                Facilitates collaboration with foreign customs entities on high-risk cargo.
              </Typography>
              <Typography variant="caption" sx={{ color: '#F57C00', fontWeight: 'bold', mt: 2, display: 'block' }}>
                Click for details →
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card 
            sx={{ 
              height: '100%',
              border: '2px solid #7B1FA2',
              cursor: 'pointer',
              '&:hover': { 
                boxShadow: 6,
                transform: 'translateY(-4px)',
                transition: 'all 0.3s ease'
              }
            }}
            onClick={() => navigate('/admin/ats-global-system')}
            role="button"
            tabIndex={0}
            aria-label="View ATS-Global details"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/admin/ats-global-system');
              }
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#7B1FA2' }}>
                ATS-Global (ATS-G)
              </Typography>
              <Typography variant="body2">
                Exports targeting technology to international partners for shared security vetting.
              </Typography>
              <Typography variant="caption" sx={{ color: '#7B1FA2', fontWeight: 'bold', mt: 2, display: 'block' }}>
                Click for details →
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card 
            sx={{ 
              border: '2px solid #D32F2F',
              cursor: 'pointer',
              '&:hover': { 
                boxShadow: 6,
                transform: 'translateY(-4px)',
                transition: 'all 0.3s ease'
              }
            }}
            onClick={() => navigate('/admin/ats-entitlement-system')}
            role="button"
            tabIndex={0}
            aria-label="View ATS Entitlement System details"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/admin/ats-entitlement-system');
              }
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#D32F2F' }}>
                Entitlement System
              </Typography>
              <Typography variant="body2">
                Central identity and access management service for ATS applications; provides Role-Based Access Control 
                (RBAC) and Active Directory integration.
              </Typography>
              <Typography variant="caption" sx={{ color: '#D32F2F', fontWeight: 'bold', mt: 2, display: 'block' }}>
                Click to view detailed subsystem information →
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Data Feeds */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <StorageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Key Data Feeds and Sources
      </Typography>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Imports and Exports:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Automated Commercial System (ACS)</Typography></li>
                <li><Typography variant="body2">Automated Export System (AES)</Typography></li>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Passenger/Visa Data:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Arrival and Departure Information System (ADIS)</Typography></li>
                <li><Typography variant="body2">SEVIS, Visa Waiver, EVUS</Typography></li>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Law Enforcement Feeds:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Treasury Enforcement Communications System (TECS)</Typography></li>
                <li><Typography variant="body2">FBI Terrorist Screening Database (TSDB)</Typography></li>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Commercial/Carrier Data:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Passenger Name Record (PNR)</Typography></li>
                <li><Typography variant="body2">Cargo manifests, ship and aircraft tracking (AIS/VMS)</Typography></li>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                CBP/OGA Systems:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Global Enrollment System (GES), Trusted Worker, CTPAT, PRIDE</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data Dictionary */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        ATS Data Dictionary (Condensed)
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Field Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Description</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Source System</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Data Type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>Traveler_ID</strong></TableCell>
              <TableCell>Unique identifier for travelers matched across systems via Unified Person schema</TableCell>
              <TableCell>ADIS / TECS</TableCell>
              <TableCell>Alphanumeric</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Shipment_ID</strong></TableCell>
              <TableCell>System-generated cargo identifier tied to manifest and entry summaries</TableCell>
              <TableCell>ACS / AES</TableCell>
              <TableCell>Alphanumeric</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Entity_Name</strong></TableCell>
              <TableCell>Individual or organization name (importer/exporter, passenger, carrier)</TableCell>
              <TableCell>TECS / PNR / AES</TableCell>
              <TableCell>Text</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Address / Port_Code</strong></TableCell>
              <TableCell>Registered location or POE code where entry occurs</TableCell>
              <TableCell>CBP Port Profile / TECS</TableCell>
              <TableCell>Text / Numeric</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Conveyance_Type</strong></TableCell>
              <TableCell>Transport mode: air, land, sea, rail</TableCell>
              <TableCell>Manifest / PNR</TableCell>
              <TableCell>Text</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Bill_of_Lading_No</strong></TableCell>
              <TableCell>Trade document identifier for cargo shipments</TableCell>
              <TableCell>ACS / AES</TableCell>
              <TableCell>String</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Risk_Score</strong></TableCell>
              <TableCell>Composite, rules-based assessment (numeric 1–1000). Higher indicates greater risk</TableCell>
              <TableCell>ATS algorithmic model</TableCell>
              <TableCell>Integer</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Risk_Reason_Code</strong></TableCell>
              <TableCell>Encoded justification based on rule or watchlist hit</TableCell>
              <TableCell>Internal ATS rule engine</TableCell>
              <TableCell>String</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Watchlist_Flag</strong></TableCell>
              <TableCell>Indicates presence on terrorist or enforcement database</TableCell>
              <TableCell>TSDB / TECS</TableCell>
              <TableCell>Boolean</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Entry_Date / Time</strong></TableCell>
              <TableCell>Time-stamped event of entry, departure, or examination</TableCell>
              <TableCell>CBP Entry System</TableCell>
              <TableCell>DateTime</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>SEVIS_Status / Visa_Type</strong></TableCell>
              <TableCell>Immigration status for nonimmigrant travelers</TableCell>
              <TableCell>SEVIS / ADIS</TableCell>
              <TableCell>String</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Exam_Result</strong></TableCell>
              <TableCell>Outcome of inspection or determination</TableCell>
              <TableCell>ATS / CBP report</TableCell>
              <TableCell>Text</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>User_Access_Level</strong></TableCell>
              <TableCell>Privilege class controlling data access within ATS</TableCell>
              <TableCell>Entitlement System</TableCell>
              <TableCell>String</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Data Volume and Architecture */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <StorageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Data Volume and Architecture
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                400+ TB
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Transactional Database
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                30-40B
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Daily Transactions
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                4+ PB
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Hadoop File Systems
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                40B+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Elastic Documents
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Interoperability
          </Typography>
          <Typography variant="body2">
            Integrated via APIs and data loaders with dozens of DHS, FAA, and foreign systems. 40+ billion documents 
            across 40+ data sources indexed for real-time querying and analysis.
          </Typography>
        </CardContent>
      </Card>

      {/* Integration and Intelligence Use */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Integration and Intelligence Use
      </Typography>

      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="body1" paragraph>
            ATS serves as a <strong>real-time fusion engine</strong> combining machine learning, rules-based risk modeling, 
            and multi-agency watchlist matching. Analysts and officers can generate ad hoc queries, visualize cargo threat 
            patterns, and receive automated system-to-system alerts shared across DHS, ICE, TSA, and Coast Guard networks.
          </Typography>
          <Typography variant="body2">
            A digital data dictionary file (CSV/JSON format) for internal architecture typically includes 12–15 core entity 
            groups (Travelers, Cargo, Entities, Risk Scores, Conveyances, Watchlists, Ports, Users, etc.).
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ATSSystemDetail;
